import { capitalizeWords } from '@zoocasa/node-kit/strings';
import { Breadcrumb } from 'components/breadcrumbs';
import Listing from 'data/listing';
import { HrefLangLink } from '../../area-listings/util/head-tags-builder';
import { generateCanonicalUrl } from 'utils/urls/canonicalUrls';
import dayjs from 'dayjs';
import { CountryCodeList } from 'types/countries';
import { countryCodeFromProvinceOrState } from 'utils/province_or_state';
import { formatI18nCurrency, getCurrencyFromLocale } from 'utils/i18n-format-currency';
import { SchemaBuilder } from '../../area-listings/util/head-tags-builder/types/schema-builder';
import { resolveSchemaLocale } from 'utils/schema/schema-locale-resolver';
import { applySeoRules } from 'utils/seo';
import { ADDRESS_ROUTE } from 'components/dynamic-page/route-matchers';

import type { TenantId, SEOConfig } from '@tenants/types';
import type { TFunction } from 'i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';
import { getStatusLabelKey, getSeoPropertyTypeLabelKey } from 'types/reso';
import { decode } from 'html-entities';

/**
 * Generates a meta description for a real estate listing page based on the listing's status and details.
 *
 * This function creates SEO-friendly meta descriptions that vary depending on whether the listing
 * is available, off-market, or sold/rented. The description includes key information like bedroom count,
 * property type, address, city, and price to help with search engine optimization.
 *
 * @param isAvailable Whether the listing is currently available for purchase or rent
 * @param isOffMarket Whether the listing is off-market (not actively listed)
 * @param isRental Whether the listing is a rental property (true for rental, false for sale, null if unknown)
 * @param bedrooms The number of bedrooms in the property
 * @param address The street address of the property (can be null)
 * @param city The city where the property is located (can be null)
 * @param priceLabel The formatted price display (e.g., "$500,000" or "$2,500/month")
 * @param home The type of home/property (e.g., "house", "condo", "townhouse"). If null or undefined, defaults to "Real Estate"
 *
 * @returns A formatted meta description string optimized for SEO
 *
 * @example
 * // Available house for sale
 * getListingPageDescription(true, false, false, 3, "123 Main St", "Toronto", "$750,000", "house")
 * // Returns: "3 Bedroom House has been listed for sale at 123 Main St in Toronto for $750,000. Chat with a local expert, book a tour."
 *
 * @example
 * // Off-market property
 * getListingPageDescription(false, true, null, 2, "456 Oak Ave", "Vancouver", undefined, "condo")
 * // Returns: "Browse photos, facts and stats for real estate listings near 456 Oak Ave, Vancouver."
 *
 * @example
 * // Sold property
 * getListingPageDescription(false, false, false, 4, "789 Pine Rd", "Calgary", "$850,000", "townhouse")
 * // Returns: "4 Bedroom Townhouse at 789 Pine Rd has been sold for $850,000. Click here to view details, map, and photos."
 */
export function getListingPageDescription(isAvailable: boolean, isOffMarket: boolean, isRental: boolean | null, bedrooms: number, address: string | null, city: string | null, priceLabel?: string, home?: string | null) {
  let metaDescription: string;
  if (!home) {
    home = 'Real Estate';
  }

  if (home.includes('house-')) {
    home = 'house';
  }
  if (isAvailable) {
    metaDescription = `${bedrooms} Bedroom ${capitalizeWords(home)} has been listed for ${isRental ? 'rent' : 'sale'} at ${address} in ${city} for ${priceLabel}. Chat with a local expert, book a tour.`;
  } else if (isOffMarket) {
    metaDescription = `Browse photos, facts and stats for real estate listings near ${address}, ${city}.`;
  } else {
    metaDescription = `${bedrooms} Bedroom ${capitalizeWords(home)} at ${address} has been ${isRental ? 'rented' : 'sold'} for ${priceLabel}. Click here to view details, map, and photos.`;
  }
  return metaDescription;
}

/**
 * Generates a page title for a real estate listing page optimized for SEO.
 *
 * This function creates a structured page title that follows SEO best practices for real estate listings.
 * The title format includes the street address, listing status, city, and tenant/brand name to improve
 * search engine visibility and user experience.
 *
 * @param tenantName The name of the tenant (e.g., "Zoocasa", "eXp Realty")
 * @param statusLabel The current status of the listing (e.g., "For Sale", "Sold", "For Rent", "Rented")
 * @param seoStreet The street address formatted for SEO (can be null)
 * @param city The city where the property is located (can be null)
 *
 * @returns A formatted page title string optimized for SEO and user experience
 *
 * @example
 * // Active listing
 * getListingPageTitle("Zoocasa", "For Sale", "123 Main Street", "Toronto")
 * // Returns: "123 Main Street, For Sale in Toronto - Zoocasa"
 *
 * @example
 * // Sold property
 * getListingPageTitle("eXp Realty", "Sold", "456 Oak Avenue", "Vancouver")
 * // Returns: "456 Oak Avenue, Sold in Vancouver - eXp Realty"
 *
 * @example
 * // Rental property
 * getListingPageTitle("eXp Realty", "For Rent", "789 Pine Road", "Calgary")
 * // Returns: "789 Pine Road, For Rent in Calgary - eXp Realty"
 *
 * @example
 * // With null values
 * getListingPageTitle("Zoocasa", null, null, "Toronto")
 * // Returns: "null, null in Toronto - Zoocasa"
 */
export function getListingPageTitle(tenantName: string, statusLabel: string | null, seoStreet: string | null, city: string | null) {
  return `${seoStreet}, ${statusLabel} in ${city} - ${tenantName}`;
}

type ListingSeoInfo = Pick<Listing, 'id' | 'isAvailable' | 'standardStatus' | 'seoType' | 'isRental' | 'seoStreet' | 'city' | 'province' | 'price' | 'bedrooms' | 'postalCode' | 'isPrivate' | 'mlsNum' | 'imageUrl' | 'addressPath' | 'position' | 'virtualTourUrl' | 'addedAt' | 'brokerage' | 'homeType' | 'sqftLabel' | 'bathrooms' | 'parking' | 'taxes' | 'daysOnMarket' | 'isOffMarket'>;

export interface GetHeadTagsParams {
  /** The name of the tenant (e.g., "Zoocasa", "eXp Realty") */
  tenantName: string;
  /** The base URL for the schema (e.g., https://www.zoocasa.com) */
  schemaUrl: string;
  /** The listing information for SEO */
  listing: ListingSeoInfo;
  /** Breadcrumb navigation items */
  breadcrumbs: readonly Breadcrumb[];
  /** The locale code (e.g., 'en-CA', 'fr-CA') */
  locale: string;
  /** The tenant ID */
  tenantId: TenantId;
  /** Optional hreflang links for internationalization */
  hrefLangs?: HrefLangLink[];
  /** Optional SEO config for applying tenant-specific SEO rules */
  seoConfig?: SEOConfig;
  /** 
   * Translation function for server-side translation.
   * When provided, title and metaDescription will be translated directly.
   * When not provided, falls back to legacy English strings.
   */
  t?: TFunction;
}

export function getHeadTags(params: GetHeadTagsParams) {
  const { tenantName, schemaUrl, listing, breadcrumbs, hrefLangs, locale, tenantId, seoConfig, t } = params;
  const { isAvailable, standardStatus, seoType, isRental, seoStreet, city, province, price, bedrooms, postalCode, isPrivate,
    mlsNum, imageUrl, addressPath, position, virtualTourUrl, addedAt,
    brokerage, homeType, isOffMarket } = listing;

  // Resolve locale for schemas based on tenant SEO config
  const localeForSchema = resolveSchemaLocale(tenantId, locale);

  const listingImage = imageUrl || `${schemaUrl}/next/assets/images/banner.png`;
  
  // Format price with i18n support
  const currency = getCurrencyFromLocale(locale);
  const priceLabel = price ? formatI18nCurrency(price, locale, currency) : '';
  const pricePrivacyLabel = isPrivate ? priceLabel?.replace(/[0-9]/g, 'X') : priceLabel;

  // not sure why but it seems that using the original value results in
  // &nbsp; instead of a space, so this is necessary
  const sanitizedPriceLabel = pricePrivacyLabel 
    ? decode(pricePrivacyLabel).replace(/\u00A0/g, ' ')
    : '';

  // Get translation keys
  const statusKey = getStatusLabelKey(standardStatus, isRental);
  const homeKey = getSeoPropertyTypeLabelKey(seoType);
  
  let title: string;
  let metaDescription: string;

  if (t) {
    // Server-side translation using provided t function
    title = t(LOCALE_COMMON_PATHS.ADDRESS_PAGE_TITLE, {
      seoStreet: seoStreet ?? '',
      statusKey,
      city: city ?? '',
      tenantName,
    });
    
    if (isAvailable) {
      metaDescription = t(LOCALE_COMMON_PATHS.ADDRESS_PAGE_DESCRIPTION_AVAILABLE, {
        bedrooms,
        homeKey,
        address: seoStreet ?? '',
        city: city ?? '',
        priceLabel: sanitizedPriceLabel,
        context: isRental ? 'rent' : 'sale',
      });
    } else if (isOffMarket) {
      metaDescription = t(LOCALE_COMMON_PATHS.ADDRESS_PAGE_DESCRIPTION_OFF_MARKET, {
        address: seoStreet ?? '',
        city: city ?? '',
      });
    } else {
      metaDescription = t(LOCALE_COMMON_PATHS.ADDRESS_PAGE_DESCRIPTION_SOLD, {
        bedrooms,
        homeKey,
        address: seoStreet ?? '',
        priceLabel: sanitizedPriceLabel,
        context: isRental ? 'rented' : 'sold',
      });
    }
  } else {
    // Fallback to legacy English strings when t function is not provided
    const statusLabel = isAvailable ? (isRental ? 'For Rent' : 'For Sale') : (isRental ? 'Rented' : 'Sold');
    title = getListingPageTitle(tenantName, statusLabel, seoStreet, city);
    metaDescription = getListingPageDescription(isAvailable, isOffMarket, isRental, bedrooms, seoStreet, city, sanitizedPriceLabel, homeType);
  }

  const residenceSchema = new SchemaBuilder()
    .setContext('https://schema.org')
    .setType('Residence')
    .setProperties({
      address: {
        '@type': 'PostalAddress',
        streetAddress: seoStreet,
        addressLocality: city,
        addressRegion: province,
        postalCode: postalCode,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: position ? position.latitude.toString() : '',
        longitude: position ? position.longitude.toString() : '',
      },
    })
    .setInLanguage(localeForSchema)
    .build();

  const finalSchema: Record<string, unknown>[] = [residenceSchema];
  if (!isPrivate || isOffMarket) {
    const breadcrumbItemList = breadcrumbs.map(({ name, link }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': link,
        name,
      },
    }));
    const breadcrumbSchema = new SchemaBuilder()
      .setContext('https://schema.org')
      .setType('BreadcrumbList')
      .setProperty('itemListElement', breadcrumbItemList)
      .setInLanguage(localeForSchema)
      .build();
    finalSchema.push(breadcrumbSchema);
  }

  if (!isOffMarket) {
    const currency = countryCodeFromProvinceOrState(province) === CountryCodeList.UNITED_STATES ? 'USD' : 'CAD';

    const offers = {
      '@type': 'Offer',
      price: price,
      priceCurrency: currency,
      url: `${schemaUrl}${addressPath}`,
    };
    const productSchema = new SchemaBuilder()
      .setContext('https://schema.org')
      .setType('Product')
      .setProperties({
        description: metaDescription,
        name: title,
        image: listingImage,
        sku: mlsNum,
        offers: {
          ...offers,
          priceValidUntil: dayjs().endOf('month').toString(),
        },
        brand: brokerage,
      })
      .setInLanguage(localeForSchema)
      .build();

    finalSchema.push(productSchema);

    if (virtualTourUrl) {
      const eventSchema = new SchemaBuilder()
        .setContext('https://schema.org')
        .setType('Event')
        .setProperties({
          description: `Take a 3D tour of ${seoStreet}`,
          name: 'Virtual Tour Available',
          image: listingImage,
          offers: { ...offers, validFrom: dayjs(addedAt).toString() },
          startDate: dayjs(addedAt).toString(),
          endDate: dayjs().endOf('month').toString(),
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
          eventStatus: 'https://schema.org/EventScheduled',
          url: `${schemaUrl}${addressPath}`,
          location: [{
            '@type': 'VirtualLocation',
            url: virtualTourUrl,
          }],
          organizer: {
            name: tenantName,
            url: `${schemaUrl}${addressPath}`,
          },
          performer: `${tenantName} Agent`,
        })
        .setInLanguage(localeForSchema)
        .build();
      finalSchema.push(eventSchema);
    }
  }

  const headTags = {
    title,
    metaDescription,
    canonicalUrl: generateCanonicalUrl({
      asPath: addressPath,
      baseHost: schemaUrl,
      locale,
      tenantId,
    }),
    placePostalCode: postalCode,
    placeStreetAddress: seoStreet,
    placeLocality: city,
    placeRegion: province,
    ogImage: listingImage,
    ogType: 'place',
    ogUrl: generateCanonicalUrl({
      asPath: addressPath,
      baseHost: schemaUrl,
      locale,
      tenantId,
    }),
    schema: finalSchema,
    hrefLangs,
  };

  // Apply tenant-specific SEO rules if config is provided
  return applySeoRules(headTags, ADDRESS_ROUTE, seoConfig);
}

